import { confirmApp } from '../common/appDialog';
import React, { useState } from 'react';
import { SUITE_DETAILS, ICONS } from './constants';
import type { CustomIndustry, Page, NavParams, CompetencyRecommendationResult, EnrolledCourse } from '@types';
import { generateCompetencyRecommendations, recommendSuitesForIndustry } from '@common/services/geminiService';
import Loader from './Loader';
import ApiErrorDisplay from './ApiErrorDisplay';
import { createSafeMarkdown } from '../utils/safeMarkdown';

const md = createSafeMarkdown();

interface CustomIndustryBuilderProps {
    onSave: (industry: CustomIndustry) => void;
    onBack: () => void;
    customIndustries: CustomIndustry[];
    onDelete: (industryName: string) => void;
    onNavigate: (nav: { page: Page; params?: NavParams }) => void;
}

const CustomIndustryBuilder: React.FC<CustomIndustryBuilderProps> = ({ onSave, onBack, customIndustries, onDelete, onNavigate }) => {
    const [step, setStep] = useState(1); // 1: Define Type, 2: Select Suites, 3: Analyzing Competencies, 4: View Competencies
    const [industryType, setIndustryType] = useState('');
    const [selectedSuites, setSelectedSuites] = useState<Set<string>>(new Set());
    const [error, setError] = useState<string | null>(null);
    const [recommendations, setRecommendations] = useState<CompetencyRecommendationResult | null>(null);
    const [isLoading, setIsLoading] = useState(false); // For final competency analysis
    const [isRecommending, setIsRecommending] = useState(false); // For suite recommendation analysis


    const handleDelete = async (industryNameToDelete: string) => {
        if (await confirmApp(`Are you sure you want to permanently delete the '${industryNameToDelete}' industry profile? This action cannot be undone.`)) {
            onDelete(industryNameToDelete);
        }
    };

    const handleRecommendSuites = async () => {
        const trimmedName = industryType.trim();
        if (!trimmedName) {
            setError('Industry Type is required.');
            return;
        }
        if (customIndustries.some(ci => ci.name.toLowerCase() === trimmedName.toLowerCase())) {
            setError('An industry with this name already exists.');
            return;
        }
        setError(null);
        setIsRecommending(true);
        try {
            const recommendedKeys = await recommendSuitesForIndustry(trimmedName, SUITE_DETAILS);
            setSelectedSuites(new Set(recommendedKeys));
            setStep(2);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setIsRecommending(false);
        }
    };
    
    const handleFinalizeAndAnalyze = async () => {
        const trimmedName = industryType.trim();
        if (selectedSuites.size === 0) {
            setError('Please select at least one suite.');
            return;
        }

        setError(null);
        setIsLoading(true);
        setStep(3);

        try {
            const newIndustry: CustomIndustry = {
                id: trimmedName.toLowerCase().replace(/\s+/g, '-') + '-' + Date.now(),
                name: trimmedName,
                suites: Array.from(selectedSuites),
            };
            onSave(newIndustry);
            const result = await generateCompetencyRecommendations(newIndustry, SUITE_DETAILS);
            setRecommendations(result);
            setStep(4);
        } catch (e: any) {
            setError(e.message);
            setStep(2); // Go back to suite selection on error
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleSuite = (suiteKey: string) => {
        const newSelection = new Set(selectedSuites);
        if (newSelection.has(suiteKey)) {
            newSelection.delete(suiteKey);
        } else {
            newSelection.add(suiteKey);
        }
        setSelectedSuites(newSelection);
    };

    const selectableSuites = Object.entries(SUITE_DETAILS);

    const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
        const colors = {
            High: 'bg-red-100 text-red-800',
            Medium: 'bg-yellow-100 text-yellow-800',
            Low: 'bg-green-100 text-green-800',
        };
        return <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${colors[priority as keyof typeof colors] || 'bg-slate-100 text-slate-800'}`}>{priority}</span>;
    };
    
    if (step === 3) {
        return <Loader message="Analyzing industry and generating competency recommendations..." />;
    }

    if (step === 4 && recommendations) {
        return (
             <div className="animate-fade-in max-w-4xl mx-auto">
                <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
                    <h2 className="text-2xl font-bold text-slate-800">Competency Recommendations for "{industryType}"</h2>
                    <p className="text-slate-500 mt-1">Based on your selections, Insights recommends the following training pathway.</p>
                    
                    <div className="mt-6">
                        <h3 className="text-lg font-semibold text-slate-700">Analysis Rationale</h3>
                        <div className="prose prose-slate max-w-none text-sm mt-2 p-4 bg-slate-50 rounded-lg border" dangerouslySetInnerHTML={{ __html: md.render(recommendations.analysisRationale || '') }} />
                    </div>

                    <div className="mt-6">
                        <h3 className="text-lg font-semibold text-slate-700">Recommended Modules</h3>
                        <div className="mt-2 border rounded-lg overflow-hidden">
                            <table className="min-w-full divide-y divide-slate-200">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Level</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Module</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Priority</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-slate-200">
                                    {recommendations.recommendedModules.map((mod, index) => (
                                        <tr key={index}>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-500">{mod.level}</td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-slate-900">{mod.module}</td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm"><PriorityBadge priority={mod.priority} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="mt-8 pt-4 border-t border-slate-200 flex flex-col sm:flex-row justify-end gap-3">
                        <button onClick={onBack} className="bg-slate-200 hover:bg-slate-300 text-slate-800 font-semibold py-2 px-4 rounded-md transition-colors">
                            Finish & Return to Hub
                        </button>
                        <button
                            onClick={() => onNavigate({ page: 'suiteDashboard', params: { suiteName: 'Learning Management Suite', workflow: 'competencyBuilder', industryName: industryType, recommendations: recommendations }})}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-md transition-colors flex items-center justify-center"
                        >
                            Proceed to Competency Builder <span className="ml-2">&rarr;</span>
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="animate-fade-in max-w-4xl mx-auto">
            <button
                onClick={onBack}
                className="flex items-center text-sm font-medium text-slate-600 hover:text-blue-600 mb-6 transition-colors duration-200"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Knowledge Hub
            </button>
            <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
                {step === 1 && (
                    <>
                        <h2 className="text-2xl font-bold text-slate-800">Create Your Custom Industry</h2>
                        <p className="text-slate-500 mt-1">Define an industry type, and let Insights provide a recommended suite of tools to start.</p>
                        
                        <div className="mt-6 space-y-4">
                            <div>
                                <label htmlFor="industryType" className="block text-sm font-medium text-slate-700">Industry Type</label>
                                <p className="text-xs text-slate-500 mb-1">Give your custom dashboard a unique and descriptive name.</p>
                                <input
                                    type="text"
                                    id="industryType"
                                    value={industryType}
                                    onChange={(e) => setIndustryType(e.target.value)}
                                    placeholder="e.g., Global Logistics Security"
                                    className="mt-1 block w-full bg-slate-50 border border-slate-300 rounded-md p-2"
                                />
                            </div>
                            <button
                                onClick={handleRecommendSuites}
                                disabled={isRecommending || !industryType.trim()}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-md disabled:bg-slate-400"
                            >
                                {isRecommending ? 'Analyzing Industry...' : 'Analyze & Get Recommendations'}
                            </button>
                        </div>
                        
                        {customIndustries.length > 0 && (
                            <div className="mt-8 pt-6 border-t border-slate-200">
                                <h3 className="text-lg font-semibold text-slate-700">Manage Existing Industries</h3>
                                <div className="mt-4 space-y-3">
                                    {customIndustries.map(industry => (
                                        <div key={industry.name} className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200">
                                            <span className="font-medium text-slate-800">{industry.name}</span>
                                            <button
                                                onClick={() => handleDelete(industry.name)}
                                                className="text-red-500 hover:text-red-700 p-1.5 rounded-full hover:bg-red-100 transition-colors"
                                                aria-label={`Delete ${industry.name}`}
                                            >
                                                {React.cloneElement(ICONS.trash as any, { className: 'h-5 w-5' })}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
                {step === 2 && (
                    <>
                        <button onClick={() => setStep(1)} className="flex items-center text-sm font-medium text-slate-600 hover:text-blue-600 mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                            Back to Industry Type
                        </button>
                        <h2 className="text-2xl font-bold text-slate-800">Step 2: Customize Your Suites</h2>
                        <p className="text-slate-500 mt-1">Insights has recommended the following suites for "{industryType}". You can add or remove suites to finalize your dashboard.</p>
                        
                        <div className="mt-6">
                            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                                {selectableSuites.map(([key, suite]) => (
                                    <label
                                        key={key}
                                        className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                            selectedSuites.has(key) ? 'border-blue-600 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedSuites.has(key)}
                                            onChange={() => handleToggleSuite(key)}
                                            className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <div className="ml-3">
                                            <p className="font-semibold text-slate-800">{suite.name}</p>
                                            <p className="text-xs text-slate-500">{suite.description}</p>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                        
                        <div className="mt-8 pt-4 border-t border-slate-200 flex justify-end">
                            <button
                                onClick={handleFinalizeAndAnalyze}
                                disabled={isLoading}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-md disabled:bg-slate-400"
                            >
                                {isLoading ? 'Saving...' : 'Finalize Suites & Get Training Plan'}
                            </button>
                        </div>
                    </>
                )}
                <ApiErrorDisplay error={error} serviceName="Custom Industry Builder" />
            </div>
        </div>
    );
};

export default CustomIndustryBuilder;
